import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import PhotoCapture from './PhotoCapture';
import SignaturePad from './SignaturePad';
import { applies, todayIso } from '../../utils/deliveryApi';

const PHASE_LABEL = { pred: 'Před odjezdem', misto: 'Na místě', po: 'Po návratu' };

const IssueNote = ({ value, onSave, disabled }) => {
  const [text, setText] = useState(value || '');
  return (
    <div className="dlv-issue">
      <textarea className="dlv-input" rows={2} value={text} disabled={disabled} placeholder="Popište výhradu / závadu (povinné)…"
        onChange={(e) => setText(e.target.value)} onBlur={() => text !== (value || '') && onSave(text)} aria-label="Popis výhrady" />
    </div>
  );
};

const SignOff = ({ step, so, technician, onStep, disabled }) => {
  const name = so.checked_by_name || technician || '';
  return (
    <div className="dlv-signoff">
      <label>
        <span className="dlv-label">Zkontroloval</span>
        <input className="dlv-input" value={so.checked_by_name || ''} disabled={disabled} placeholder={technician || 'Jméno'}
          onChange={(e) => onStep(step, { checked_by_name: e.target.value, checked_date: so.checked_date || todayIso() })} />
      </label>
      <label>
        <span className="dlv-label">Datum</span>
        <div style={{ display: 'flex', gap: 6 }}>
          <input className="dlv-input" type="date" value={so.checked_date || ''} disabled={disabled}
            onChange={(e) => onStep(step, { checked_by_name: name, checked_date: e.target.value })} />
          {!disabled && <button type="button" className="dlv-iconbtn" onClick={() => onStep(step, { checked_by_name: name, checked_date: todayIso() })}>Dnes</button>}
        </div>
      </label>
    </div>
  );
};

// One step of the checklist: items (check / výhrada / photo), step photos, sign-off, signatures (5) and notes (7)
const ChecklistStep = ({ step, protocol, onItem, onStep, onNotes, reload, disabled }) => {
  const model = protocol.header?.model;
  const photosFor = (key) => protocol.photos.filter((p) => Number(p.step) === step.step && (p.item_key || null) === key);
  const [notes, setNotes] = useState(protocol.notes || '');

  return (
    <section className="dlv-card" aria-labelledby={`dlv-step-${step.step}`}>
      <div className="dlv-section-head">
        <span className="dlv-section-head__num">{String(step.step).padStart(2, '0')}</span>
        <h2 id={`dlv-step-${step.step}`} className="dlv-section-head__title">{step.title}</h2>
        <span className="dlv-section-head__phase">{PHASE_LABEL[step.phase]}</span>
      </div>
      <p className="dlv-hint">{step.hint}</p>

      <ul className="dlv-items">
        {step.items.map((item) => {
          const st = protocol.items?.[item.key] || {};
          const na = !applies(item, model);
          const itemPhotos = photosFor(item.key);
          return (
            <li key={item.key} className={`dlv-item ${na ? 'dlv-item--na' : ''}`}>
              <div className="dlv-item__row">
                <label className="dlv-check">
                  <input type="checkbox" checked={Boolean(st.checked)} disabled={disabled || na}
                    onChange={(e) => onItem(item.key, { checked: e.target.checked })} />
                  <span className="dlv-check__box" aria-hidden="true" />
                  <span>{item.label}{na && <em style={{ color: '#6b6b6b', fontStyle: 'normal' }}> – nevztahuje se (TR3)</em>}</span>
                </label>
                {item.tr4 && <span className="dlv-tag">TR4</span>}
                {!na && (
                  <>
                    <button type="button" className={`dlv-iconbtn ${st.issue ? 'dlv-iconbtn--on' : ''}`} disabled={disabled}
                      onClick={() => onItem(item.key, { issue: !st.issue })} aria-pressed={Boolean(st.issue)}>
                      <AlertTriangle size={16} /> Výhrada
                    </button>
                    <PhotoCapture compact protocolId={protocol.id} step={step.step} itemKey={item.key} photos={itemPhotos}
                      onChange={reload} disabled={disabled} label={`Vyfotit: ${item.label}`} />
                  </>
                )}
              </div>
              {st.issue && <IssueNote value={st.issue_note} disabled={disabled} onSave={(t) => onItem(item.key, { issue_note: t })} />}
              {itemPhotos.length > 0 && <PhotoCapture protocolId={protocol.id} step={step.step} itemKey={item.key} photos={itemPhotos} onChange={reload} disabled={disabled} hideButton />}
            </li>
          );
        })}
      </ul>

      <div style={{ marginTop: 18 }}>
        <span className="dlv-label">Fotodokumentace ke kroku</span>
        <PhotoCapture protocolId={protocol.id} step={step.step} photos={photosFor(null)} onChange={reload} disabled={disabled} label="Vyfotit / přidat fotky" />
      </div>

      {step.step === 5 && (
        <div className="dlv-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', marginTop: 20 }}>
          <SignaturePad protocolId={protocol.id} role="technician" label="Podpis technika BASICGAME" signature={protocol.signatures?.technician}
            defaultName={protocol.header?.technician_name} onSaved={reload} disabled={disabled} />
          <SignaturePad protocolId={protocol.id} role="customer" label="Podpis za zákazníka" signature={protocol.signatures?.customer}
            defaultName={protocol.header?.contact_name} onSaved={reload} disabled={disabled} />
        </div>
      )}

      {step.step === 7 && (
        <label style={{ display: 'block', marginTop: 20 }}>
          <span className="dlv-label">Poznámky</span>
          <textarea className="dlv-input" rows={5} value={notes} disabled={disabled} onChange={(e) => setNotes(e.target.value)}
            onBlur={() => notes !== (protocol.notes || '') && onNotes(notes)} />
        </label>
      )}

      <SignOff step={step.step} so={protocol.steps?.[step.step] || {}} technician={protocol.header?.technician_name} onStep={onStep} disabled={disabled} />
    </section>
  );
};

export default ChecklistStep;
